import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  DEFAULT_DEVICE_MODELS,
  fetchDocumentAssignments,
  fetchRegisteredMachines,
  filterMachinesBySerialQuery,
  formatDocumentLabel,
  getAssignmentSerial,
  getDeviceDocumentsErrorMessage,
  getDeviceDocumentsErrorStatus,
  getMachineShortSerial,
  isActiveDocument,
  isReadyDocument,
} from '../../helper/DeviceDocumentsHelper';

const SERIAL_MENU_LIMIT = 80;

const formatVersionOption = (doc) => {
  if (!doc) return '';
  if (doc.version && doc.title) return `${doc.version} — ${doc.title}`;
  return doc.version || formatDocumentLabel(doc);
};

const SerialSearchSelect = ({
  machines,
  value,
  query,
  disabled,
  loading,
  onQueryChange,
  onSelect,
}) => {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const matches = useMemo(
    () => filterMachinesBySerialQuery(machines, query).slice(0, SERIAL_MENU_LIMIT),
    [machines, query]
  );
  const placeholder = loading
    ? 'Loading machines…'
    : 'Search 1G0331 or last 4 digits';

  useEffect(() => {
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, []);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  const pick = (machineUDI) => {
    onSelect(machineUDI);
    setOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlight((index) => Math.min(index + 1, Math.max(matches.length - 1, 0)));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === 'Enter' && open && matches[highlight]) {
      event.preventDefault();
      pick(matches[highlight].machineUDI);
      return;
    }
    if (event.key === 'Escape') setOpen(false);
  };

  return (
    <div className={`docs-combo${open ? ' docs-combo--open' : ''}`} ref={rootRef}>
      <input
        id="docs-assign-serial"
        className="docs-search-input"
        type="text"
        value={query}
        onChange={(event) => {
          onQueryChange(event.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-controls="docs-serial-options"
        aria-autocomplete="list"
      />
      {open && !disabled ? (
        <ul id="docs-serial-options" className="docs-combo-menu" role="listbox">
          {loading ? (
            <li className="docs-combo-empty">Loading machines…</li>
          ) : matches.length === 0 ? (
            <li className="docs-combo-empty">
              {!machines.length
                ? 'No registered machines for this model.'
                : 'No matching machine number'}
            </li>
          ) : (
            matches.map((item, index) => (
              <li key={item.machineUDI} role="option" aria-selected={item.machineUDI === value}>
                <button
                  type="button"
                  className={`docs-combo-option${index === highlight ? ' docs-combo-option--active' : ''}${item.machineUDI === value ? ' docs-combo-option--selected' : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setHighlight(index)}
                  onClick={() => pick(item.machineUDI)}
                >
                  <span className="docs-combo-option-udi">{getMachineShortSerial(item.machineUDI)}</span>
                  <span className="docs-combo-option-meta">{item.machineUDI}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  );
};

const AssignVersionModal = ({
  documents,
  models = DEFAULT_DEVICE_MODELS,
  busy = false,
  defaultSerial = '',
  defaultDocumentId = '',
  defaultModel = '',
  onClose,
  onSubmit,
}) => {
  const [machines, setMachines] = useState([]);
  const [machinesLoading, setMachinesLoading] = useState(true);
  const [product, setProduct] = useState(defaultModel || '');
  const [serialQuery, setSerialQuery] = useState(defaultSerial || '');
  const [selectedUdi, setSelectedUdi] = useState(defaultSerial || '');
  const [documentId, setDocumentId] = useState(defaultDocumentId ? String(defaultDocumentId) : '');
  const [lookup, setLookup] = useState(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [error, setError] = useState('');

  const modelMachines = useMemo(
    () => machines.filter((item) => (
      !product
      || !item.deviceModel
      || item.deviceModel === product
    )),
    [machines, product]
  );
  const readyDocs = useMemo(
    () => documents.filter((doc) => isReadyDocument(doc) && (!product || doc.deviceModel === product)),
    [documents, product]
  );
  const productDefault = documents.find(
    (doc) => isReadyDocument(doc) && isActiveDocument(doc) && doc.deviceModel === product
  );

  const runLookup = (machineUDI) => {
    if (!machineUDI) {
      setLookup(null);
      return;
    }
    setLookupBusy(true);
    fetchDocumentAssignments({ machineUDI })
      .then((data) => {
        const records = Array.isArray(data?.records) ? data.records : [];
        const total = Number(data?.total ?? records.length);
        if (total === 0 || records.length === 0) {
          setLookup({ status: 'unpinned', machineUDI, record: null });
          return;
        }
        const record = { ...records[0], machineUDI: getAssignmentSerial(records[0]) || machineUDI };
        setLookup({ status: 'found', machineUDI, record });
        if (record.documentId && !documentId) setDocumentId(String(record.documentId));
      })
      .catch((err) => {
        const status = getDeviceDocumentsErrorStatus(err);
        if (status === 404) {
          setLookup({
            status: 'missing',
            machineUDI,
            message: getDeviceDocumentsErrorMessage(err, 'Serial not in registration.'),
          });
          return;
        }
        setError(getDeviceDocumentsErrorMessage(err, 'Lookup failed.'));
      })
      .finally(() => setLookupBusy(false));
  };

  useEffect(() => {
    if (!product) {
      setMachines([]);
      setMachinesLoading(false);
      return undefined;
    }

    let cancelled = false;
    setMachinesLoading(true);
    fetchRegisteredMachines(product)
      .then((list) => {
        if (cancelled) return;
        setMachines(list);
        if (!list.length) {
          setError('No registered machines found for this model.');
          return;
        }
        if (defaultSerial) {
          const match = list.find((item) => item.machineUDI === defaultSerial);
          if (match) {
            setSelectedUdi(match.machineUDI);
            setSerialQuery(match.machineUDI);
          }
        }
      })
      .catch(() => {
        if (!cancelled) setError('Unable to load registered machines.');
      })
      .finally(() => {
        if (!cancelled) setMachinesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [product, defaultSerial]);

  useEffect(() => {
    if (selectedUdi) runLookup(selectedUdi);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUdi]);

  const handleModelChange = (nextModel) => {
    setProduct(nextModel);
    setSelectedUdi('');
    setSerialQuery('');
    setLookup(null);
    setError('');
    setDocumentId('');
  };

  const handleQueryChange = (nextQuery) => {
    setSerialQuery(nextQuery);
    setSelectedUdi('');
    setLookup(null);
    setError('');
    if (!defaultDocumentId) setDocumentId('');
  };

  const handleSerialSelect = (machineUDI) => {
    setSelectedUdi(machineUDI);
    setSerialQuery(machineUDI);
    setError('');
    if (!defaultDocumentId) setDocumentId('');
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (busy) return;
    if (!product) {
      setError('Select V730i or NX5 first.');
      return;
    }
    if (!selectedUdi) {
      setError('Search and select a machine number.');
      return;
    }
    if (!documentId) {
      setError('Select a document version.');
      return;
    }
    setError('');
    onSubmit(selectedUdi, Number(documentId));
  };

  const pinHint = () => {
    if (!product) return 'Select V730i or NX5, then search a machine number.';
    if (machinesLoading) return 'Loading registered machines…';
    if (!selectedUdi) {
      return modelMachines.length
        ? `${modelMachines.length} registered machine${modelMachines.length === 1 ? '' : 's'} — click the field and pick a serial.`
        : 'No registered machines for this model in this environment.';
    }
    if (lookupBusy) return 'Looking up the current mapping…';
    if (!lookup) return 'Choose a ready version for this machine.';
    if (lookup.status === 'missing') return lookup.message || 'Serial not in registration.';
    if (lookup.status === 'unpinned') {
      return productDefault
        ? `No mapping — QR uses ${formatDocumentLabel(productDefault)}.`
        : 'No mapping — QR uses the product default if one is published.';
    }
    const version = lookup.record?.document
      ? formatDocumentLabel(lookup.record.document)
      : lookup.record?.version;
    return version ? `Currently mapped to ${version}.` : 'This machine already has a mapped document.';
  };

  return (
    <div className="modal" role="dialog" aria-modal="true" aria-labelledby="docs-assign-title">
      <div className="backdrop" onClick={busy ? undefined : onClose} aria-hidden="true" />
      <div className="modal-content docs-modal-wide">
        <h2 id="docs-assign-title">Assign version</h2>
        <p className="docs-modal-copy">
          Choose the model, then click machine number to see registered serials. Search by 1G0331 or last 4 digits.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="docs-field">
            <label htmlFor="docs-assign-product">Model</label>
            <select
              id="docs-assign-product"
              className="docs-filter-select"
              value={product}
              onChange={(event) => handleModelChange(event.target.value)}
              disabled={busy}
            >
              <option value="">Select V730i or NX5</option>
              {models.map((model) => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>

          <div className="docs-field">
            <label htmlFor="docs-assign-serial">Machine number</label>
            <SerialSearchSelect
              machines={modelMachines}
              value={selectedUdi}
              query={serialQuery}
              disabled={busy || !product}
              loading={machinesLoading}
              onQueryChange={handleQueryChange}
              onSelect={handleSerialSelect}
            />
          </div>

          <div className="docs-field">
            <label htmlFor="docs-assign-version">Version</label>
            <select
              id="docs-assign-version"
              className="docs-filter-select"
              value={documentId}
              onChange={(event) => setDocumentId(event.target.value)}
              disabled={busy || !product}
            >
              <option value="">
                {product ? 'Select version' : 'Select a model first'}
              </option>
              {readyDocs.map((doc) => (
                <option key={doc.id} value={doc.id}>
                  {formatVersionOption(doc)}
                </option>
              ))}
            </select>
          </div>

          <p className="docs-hint">{pinHint()}</p>
          {error ? <p className="error">{error}</p> : null}

          <div className="actions">
            <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
            <button
              type="submit"
              disabled={busy || lookupBusy || machinesLoading || !product || !selectedUdi || !documentId}
            >
              {busy ? 'Saving…' : 'Assign version'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AssignVersionModal;
